import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase-client';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { normalizeTeamMetadata, teamMatchesMetadataLabel } from '../components/teams/utils/teamMetadata';
import { getTeamLogoUrl } from '../components/teams/utils/teamLogo';
import '../styles/SearchForTeams.css';
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function calculateAge(dob) {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}
function formatDate(timestamp) {
  if (!timestamp?.toDate) return 'Unknown';
  const date = timestamp.toDate();
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
function getVisibleLinks(links = []) {
  const allowedTypes = ['Website', 'Facebook Page', 'Instagram', 'Twitter', 'YouTube', 'Linktree'];
  return links.filter(link => allowedTypes.includes(link.type));
}
function getDistanceFromUser(team, userLocation) {
  if (!userLocation || !team.address?.geo) return Number.POSITIVE_INFINITY;
  return haversine(userLocation.lat, userLocation.lng, team.address.geo.lat, team.address.geo.lng);
}
export default function SearchForTeams() {
  const {
    user,
    authLoading
  } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [teams, setTeams] = useState([]);
  const [search, setSearch] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('');
  const [joinableFilter, setJoinableFilter] = useState('');
  const [maxDistance, setMaxDistance] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        });
      }, err => {}, {
        enableHighAccuracy: true
      });
    }
  }, []);
  useEffect(() => {
    const fetchProfileAndTeams = async () => {
      if (!user) return;
      const [userSnap, privateSnap] = await Promise.all([
        getDoc(doc(db, 'users', user.uid)),
        getDoc(doc(db, 'privateUsers', user.uid)),
      ]);
      if (!userSnap.exists()) return;
      const userProfile = {
        ...(userSnap.data() || {}),
        dob: privateSnap.exists() ? (privateSnap.data()?.dob || '') : '',
      };
      setProfile(userProfile);
      const snapshot = await getDocs(collection(db, 'teams'));
      const allTeams = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const notJoined = allTeams.filter(team => !userProfile?.team_roles?.[team.id] && team.availability?.live !== false && team.availability?.public !== false);
      setTeams(notJoined);
    };
    fetchProfileAndTeams();
  }, [user]);
  const handleJoin = async team => {
    if (!user || !profile) return;
    const status = team?.availability?.joinable;

    // For both Open and On Approval we use the join flow page.
    if (status === 'Open' || status === 'On Approval') {
      window.location.assign(`/join/${team.id}`);
      return;
    }

    // Closed: do nothing (or show a toast)
    alert(`${team.name} is not accepting new members right now.`);
  };
  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name?.toLowerCase().includes(search.toLowerCase());
    const matchesLabel = teamMatchesMetadataLabel(team.teamMetadata || {}, labelFilter);
    const matchesAge = !ageGroupFilter || team.teamMetadata?.ageGroup === ageGroupFilter;
    const matchesJoinable = !joinableFilter || team.availability?.joinable === joinableFilter;
    const matchesDistance = !maxDistance || !userLocation || getDistanceFromUser(team, userLocation) <= maxDistance;
    const userAge = profile?.dob ? calculateAge(profile.dob) : null;
    const ageMin = team.teamMetadata?.ageRestrictions?.min;
    const ageMax = team.teamMetadata?.ageRestrictions?.max;
    const matchesEligibility = userAge === null || ((!ageMin || userAge >= ageMin) && (!ageMax || userAge <= ageMax));
    return matchesSearch && matchesLabel && matchesAge && matchesJoinable && matchesDistance && matchesEligibility;
  });
  const sortedFilteredTeams = useMemo(() => {
    return [...filteredTeams].sort((a, b) => {
      const distanceA = getDistanceFromUser(a, userLocation);
      const distanceB = getDistanceFromUser(b, userLocation);
      const aHasDistance = Number.isFinite(distanceA);
      const bHasDistance = Number.isFinite(distanceB);
      if (aHasDistance && bHasDistance) {
        const distanceDifference = distanceA - distanceB;
        if (distanceDifference !== 0) return distanceDifference;
      } else if (aHasDistance && !bHasDistance) {
        return -1;
      } else if (!aHasDistance && bHasDistance) {
        return 1;
      }
      return (a.name || '').localeCompare(b.name || '', undefined, {
        sensitivity: 'base'
      });
    });
  }, [filteredTeams, userLocation]);
  const allLabels = useMemo(() => {
    const labels = teams.flatMap((team) => normalizeTeamMetadata(team.teamMetadata || {}).labels);
    return [...new Set(labels)].sort((a, b) => a.localeCompare(b, undefined, {
      sensitivity: 'base'
    }));
  }, [teams]);
  if (authLoading) {
    return <div className="team-search-page"><p className="text-muted">Loading authentication...</p></div>;
  }
  if (!user) {
    const nextPath = `${location.pathname}${location.search || ''}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }
  return <div className="team-search-page">
      <h1 className="search-title">Search for Clubs</h1>

      <input type="text" placeholder="Search by team name..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />

      <div className="filters">
        <select value={labelFilter} onChange={e => setLabelFilter(e.target.value)}>
          <option value="">All Categories &amp; Types</option>
          {allLabels.map(label => <option key={label} value={label}>{label}</option>)}
        </select>

        <select value={ageGroupFilter} onChange={e => setAgeGroupFilter(e.target.value)}>
          <option value="">All Age Groups</option>
          {['Children', 'Teens', 'Adults', 'Mixed'].map(ag => <option key={ag} value={ag}>{ag}</option>)}
        </select>

        <select value={joinableFilter} onChange={e => setJoinableFilter(e.target.value)}>
          <option value="">All Join Statuses</option>
          {['Open', 'On Approval', 'Closed'].map(j => <option key={j} value={j}>{j}</option>)}
        </select>

        <select value={maxDistance} onChange={e => setMaxDistance(Number(e.target.value))}>
          <option value={0}>Any Distance</option>
          <option value={5}>Within 5 km</option>
          <option value={10}>Within 10 km</option>
          <option value={25}>Within 25 km</option>
          <option value={50}>Within 50 km</option>
        </select>
      </div>

      <div className="team-cards team-search-results" role="region" aria-label="Club search results" tabIndex={0}>
        {sortedFilteredTeams.map(team => {
        const normalizedMetadata = normalizeTeamMetadata(team.teamMetadata || {});
        const visibleLinks = getVisibleLinks(team.publicLinks || []);
        const isSuspicious = team.memberCount <= 2 && team.createdAt?.toDate?.() > new Date(Date.now() - 1000 * 60 * 60 * 24 * 7); // created within last 7 days
        const distance = getDistanceFromUser(team, userLocation);
        const logoUrl = getTeamLogoUrl(team);
        const joinStatus = team.availability?.joinable;
        const canJoin = joinStatus !== 'Closed';
        return <div key={team.id} className="search-team-card" style={{
          borderLeft: `5px solid ${team.brand?.primaryColor || '#ccc'}`
        }}>
              <div className={`search-team-card__header${logoUrl ? '' : ' search-team-card__header--no-logo'}`}>
                {logoUrl && <span className="search-team-card__logo">
                    <img src={logoUrl} alt={`${team.name} logo`} className="team-logo" onError={event => {
                    event.currentTarget.closest('.search-team-card__logo')?.style.setProperty('display', 'none');
                  }} />
                  </span>}
                <h3>{team.name}</h3>
              </div>
              <div className="team-info">
                <p className="search-team-card__description">{team.description || <em>No description yet</em>}</p>

                <div className="meta">
                  {isSuspicious && <span className="suspicious-flag">⚠️ New Club</span>}
                  <p><strong>Primary:</strong> {normalizedMetadata.primaryLabel || '—'}</p>
                  <p><strong>Categories:</strong> {normalizedMetadata.categories.length ? normalizedMetadata.categories.join(', ') : '—'}</p>
                  <p><strong>Types:</strong> {normalizedMetadata.types.length ? normalizedMetadata.types.join(', ') : '—'}</p>
                  <p><strong>Age Group:</strong> {team.teamMetadata?.ageGroup || '—'}</p>
                  <p><strong>Status:</strong> {team.availability?.joinable || '—'}</p>
                  <p><strong>Created:</strong> {formatDate(team.createdAt)}</p>
                  <p><strong>Members:</strong> {team.memberCount ?? 'Unknown'}</p>
                  {Number.isFinite(distance) && <p><strong>Distance:</strong> {distance.toFixed(1)} km</p>}
                </div>
                {visibleLinks.length > 0 && <div className="team-links">
                    <strong>Links:</strong>
                    <ul>
                      {visibleLinks.map((link, idx) => <li key={idx}>
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            {link.label || link.type}
                          </a>
                        </li>)}
                    </ul>
                  </div>}
              </div>
              <div className="search-team-card__footer">
                {canJoin ? <button type="button" onClick={() => handleJoin(team)} className="button button-primary join-button">
                    {joinStatus === 'On Approval' ? 'Continue to join form' : 'Join club'}
                  </button> : <p className="join-unavailable">Joining not available</p>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}
